import Delimiter from '@coolbytes/editorjs-delimiter';
import type { OutputData } from '@editorjs/editorjs';
import EditorJS from '@editorjs/editorjs';
import EditorjsList from '@editorjs/list';
import Marker from '@editorjs/marker';
import Paragraph from '@editorjs/paragraph';
import Quote from '@editorjs/quote';
import Table from '@editorjs/table';
import Underline from '@editorjs/underline';
import ColorPicker from 'editorjs-color-picker';
import DragDrop from 'editorjs-drag-drop';
import AlignmentTuneTool from 'editorjs-text-alignment-blocktune';
import Undo from 'editorjs-undo';
import React, { useEffect, useRef, useState } from 'react';
import { FaCode, FaPaste, FaSearchMinus, FaSearchPlus } from 'react-icons/fa';
import { Button, Select } from 'rizzui';
import { toast } from 'sonner';
import { ContractService } from '../../services/contract/contractService';
import { ProductService } from '../../services/product/productService';
import Title from 'title-editorjs';
import {
  ContractTemplateProductTypeContext,
  ContractTemplateVariableCategory,
  filterContractTemplateVariableCategories,
} from '../../utils/contractTemplateVariables';
import { convertEditorJSONToHTML, convertHTMLToEditorJSON } from '../../utils/editorConversions';
import './ContractTemplateEditor.css';

interface ContractTemplateWYSIWYGEditorProps {
  initialData?: OutputData | string; // Puede ser JSON de Editor.js o HTML
  title?: string;
  onChange?: (data: OutputData) => void;
  onSave?: (data: string) => void; // Cambiado a string para HTML
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  type?: 'contract' | 'email';
  variables?: ContractTemplateVariableCategory[];
  disabledSaving?: boolean;
  watermarkImage?: string | null;
  productType?: ContractTemplateProductTypeContext;
}

const ContractTemplateWYSIWYGEditor: React.FC<ContractTemplateWYSIWYGEditorProps> = ({
  initialData,
  title,
  onChange,
  onSave,
  placeholder = 'Comience a escribir su plantilla de contrato...',
  readOnly = false,
  className = '',
  type,
  variables,
  disabledSaving = false,
  watermarkImage = null,
  productType,
}) => {
  const editorRef = useRef<EditorJS | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewTemplateContent, setPreviewTemplateContent] = useState('');
  const [previewZoom, setPreviewZoom] = useState(1);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewContractOptions, setPreviewContractOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedPreviewContractId, setSelectedPreviewContractId] = useState<string | null>(null);

  // Referencia para rastrear la última versión de initialData procesada
  const lastInitialDataRef = useRef<OutputData | undefined>(undefined);
  // Referencia al contenedor del editor para eventos de foco
  const editorContainerRef = useRef<HTMLDivElement>(null);
  // Estado para rastrear si el editor debe mantener el foco
  const [shouldMaintainFocus, setShouldMaintainFocus] = useState(false);
  // Referencia para el último elemento enfocado dentro del editor
  const lastFocusedElementRef = useRef<Element | null>(null);
  const visibleVariableCategories = React.useMemo(
    () => filterContractTemplateVariableCategories(variables, productType),
    [variables, productType]
  );
  const canUseVariables = visibleVariableCategories.length > 0;

  // Procesar initialData para convertir HTML a JSON si es necesario
  const processedInitialData = React.useMemo(() => {
    if (!initialData) return undefined;

    // Si es string (HTML), convertir a JSON
    if (typeof initialData === 'string') {
      return convertHTMLToEditorJSON(initialData);
    }

    // Si ya es OutputData, usar tal como está
    return initialData;
  }, [initialData]);

  // Efecto para establecer la primera categoría como seleccionada por defecto si variables es de tipo array
  useEffect(() => {
    if (visibleVariableCategories.length === 0) {
      setSelectedCategory('');
      return;
    }

    if (!selectedCategory || !visibleVariableCategories.some((category) => category.type === selectedCategory)) {
      setSelectedCategory(visibleVariableCategories[0].type);
    }
  }, [visibleVariableCategories, selectedCategory]);

  useEffect(() => {
    const fetchPreviewContracts = async () => {
      if (type !== 'contract') {
        return;
      }

      try {
        const response = await (await (new ContractService()).listContracts({
          page: 1,
          limit: 200,
          filter_filters: {
            is_draft: false
          },
          filter_order: [
            {
              field: 'created_at',
              order: 'desc'
            }
          ]
        })).getResponseData();

        const contracts = response?.data?.data || [];
        const options = contracts.map((contract: any) => ({
          value: contract.id,
          label: contract.clientCompanyName ? `${contract.code} - ${contract.clientCompanyName}` : contract.code
        }));

        setPreviewContractOptions(options);
        if (!selectedPreviewContractId && options.length > 0) {
          setSelectedPreviewContractId(options[0].value);
        }
      } catch (error) {
        console.error('Error loading contracts for template preview:', error);
      }
    };

    fetchPreviewContracts();
  }, [type]);

  // Efecto para inicializar el editor solo una vez al montar
  useEffect(() => {
    if (!editorRef.current) {
      const editor = new EditorJS({
        holder: 'contract-editor',
        readOnly,
        placeholder,
        data: processedInitialData,
        tools: {
          title: {
            class: Title as any,
            inlineToolbar: true,

          },
          paragraph: {
            class: Paragraph as any,
            inlineToolbar: true,
          },
          list: {
            class: EditorjsList as any,
            inlineToolbar: true,
            config: {
              defaultStyle: 'unordered'
            }
          },
          /* quote: {
            class: Quote as any,
            inlineToolbar: true
          }, */
          delimiter: {
            class: Delimiter,
            config: {
              styleOptions: ['star', 'dash', 'line'],
              defaultStyle: 'line',
              lineWidthOptions: [8, 15, 25, 35, 50, 60, 100],
              defaultLineWidth: 100,
              lineThicknessOptions: [1, 2, 3, 4, 5, 6],
              defaultLineThickness: 2,
            }
          },
          table: {
            class: Table as any,
            inlineToolbar: true,
            config: {
              rows: 2,
              cols: 3
            }
          },
          Marker: {
            class: Marker,
            shortcut: 'CMD+SHIFT+M',
          },
          underline: {
            class: Underline,
            shortcut: 'CMD+SHIFT+U',
          },
          alignment: {
            class: AlignmentTuneTool as any,
            inlineToolbar: true,
          },
          ColorPicker: {
            class: ColorPicker,
            config: {
              colors: ["#FABEBE", "#E6194B", "#800000", "#F032E6", "#FFD8B1", "#F58231", "#9A6324", "#FFFAC8", "#D2F53C", "#808000", "#AAFFC3", "#3CB44B", "#46F0F0", "#008080", "#E6BEFF", "#0082C8", "#000075", "#911EB4", "#808080", "#000000"],
              columns: 5,
            },
          },
        },
        tunes: ['alignment'],
        i18n: {
          messages: {
            ui: {
              // Translations of internal UI components of the editor.js core
              "toolbar": {
                "toolbox": {
                  "Add": "Añadir",
                }
              },
              "popover": {
                "Filter": "Filtrar",
                "Nothing found": "0 resultados",
                "Convert to": "Convertir en",
              },
              "blockTunes": {
                "toggler": {
                  "Click to tune": "Configurar bloque",
                },
              },
              "inlineToolbar": {
                "converter": {
                  "Convert to": "Convertir en"
                }
              },
            },
            toolNames: {
              // Section for translation Tool Names: both block and inline tools
              "Text": "Párrafo",
              "Unordered List": "Lista desordenada",
              "Ordered List": "Lista ordenada",
              "Checklist": "Lista de checks",
              "Quote": "Cita",
              "Delimiter": "Divisor",
              "Table": "Tabla",
              "Link": "Enlace",
              "Bold": "Negrita",
              "Italic": "Cursiva",
              "Marker": "Marcador",
              "Underline": "Subrayado",
              "Title": "Título",
            },
            tools: {
              // Section for passing translations to the external tools classes
              // The first-level keys of this object should be equal of keys ot the 'tools' property of EditorConfig
              "link": {
                "Add a link": "Insertar enlace"
              },
              "table": {
                "Add row above": "Añadir fila arriba",
                "Add row below": "Añadir fila abajo",
                "Delete row": "Eliminar fila",
                "Add column to left": "Añadir columna a la izquierda",
                "Add column to right": "Añadir columna a la derecha",
                "Delete column": "Eliminar columna",
              },
              "list": {
                'Unordered': 'Desordenada',
                'Ordered': 'Ordenada',
                'Checklist': 'Checks',
              },
              "delimiter": {
                "Star": "Estrella",
                "Dash": "Guión",
                "Line 8%": "Línea 8%",
                "Line 15%": "Línea 15%",
                "Line 25%": "Línea 25%",
                "Line 35%": "Línea 35%",
                "Line 50%": "Línea 50%",
                "Line 60%": "Línea 60%",
                "Line 100%": "Línea 100%",
                "Thickness 1": "Grosor 1",
                "Thickness 2": "Grosor 2",
                "Thickness 3": "Grosor 3",
                "Thickness 4": "Grosor 4",
                "Thickness 5": "Grosor 5",
                "Thickness 6": "Grosor 6",
              },
              "quote": {
                "Quote": "Cita",
                "Enter a quote": "Ingresa una cita",
                "Enter a caption": "Ingresa un pie de cita"
              },
            },
            blockTunes: {
              // Section allows to translate Block Tunes
              "convertTo": {
                "Convert to": "Convertir a"
              },
              "delete": {
                "Delete": "Eliminar",
                "Click to delete": "Click para eliminar"
              },
              "moveUp": {
                "Move up": "Mover arriba"
              },
              "moveDown": {
                "Move down": "Mover abajo"
              },
            },
          }
        },
        onChange: async () => {
          if (onChange && editorRef.current) {
            try {
              const data = await editorRef.current.save();
              // Pasar los datos JSON originales para onChange
              onChange(data);
            } catch (error) {
              console.error('Error saving editor data:', error);
            }
          }
        },
        onReady: () => {
          setIsReady(true);
          if (editorRef.current) {
            new Undo({ editor: editorRef.current });
            new DragDrop(editorRef.current, "2px solid #fff");
          }
        }
      });

      editorRef.current = editor;
    }

    return () => {
      if (editorRef.current && editorRef.current.destroy) {
        editorRef.current.destroy();
        editorRef.current = null;
        setIsReady(false);
        setInitialDataLoaded(false);
      }
    };
  }, []); // Solo se ejecuta una vez al montar

  // Efecto separado para cargar datos iniciales solo una vez
  useEffect(() => {
    if (editorRef.current && isReady && processedInitialData && !initialDataLoaded) {
      editorRef.current.render(processedInitialData).then(() => {
        setInitialDataLoaded(true);
        lastInitialDataRef.current = processedInitialData;
      }).catch(error => {
        console.error('Error rendering initial data:', error);
      });
    }
  }, [processedInitialData, isReady, initialDataLoaded]);

  // Efecto para detectar cambios externos en initialData (ej: cargar producto diferente)
  useEffect(() => {
    if (processedInitialData !== lastInitialDataRef.current && initialDataLoaded) {
      setInitialDataLoaded(false);
      lastInitialDataRef.current = processedInitialData;
    }
  }, [processedInitialData, initialDataLoaded]);

  // Efecto para monitorear eventos de foco y detectar pérdidas de foco
  useEffect(() => {
    const container = editorContainerRef.current;
    if (!container || !isReady) return;

    const handleFocusIn = (e: FocusEvent) => {
      lastFocusedElementRef.current = e.target as Element;
      setShouldMaintainFocus(true);
    };

    const handleFocusOut = (e: FocusEvent) => {
      // Verificar si el foco se movió fuera del editor completamente
      const relatedTarget = e.relatedTarget as Node;
      if (!relatedTarget || !container.contains(relatedTarget)) {
        // Si el usuario estaba escribiendo, intentar restaurar el foco después de un breve delay
        if (shouldMaintainFocus) {
          setTimeout(() => {
            try {
              // Intentar enfocar el último elemento conocido o el contenedor del editor
              const elementToFocus = lastFocusedElementRef.current ||
                container.querySelector('[contenteditable="true"]') ||
                container.querySelector('.ce-block__content') ||
                container;

              if (elementToFocus && typeof (elementToFocus as HTMLElement).focus === 'function') {
                (elementToFocus as HTMLElement).focus();
              }
            } catch (error) {
              console.log('❌ Failed to restore focus:', error);
            }
          }, 50); // Delay mínimo para permitir que otros eventos se procesen
        }
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.altKey && !e.metaKey) {
        setShouldMaintainFocus(true);
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Si el click es fuera del editor, permitir que el foco se mueva
      if (!container.contains(e.target as Node)) {
        setShouldMaintainFocus(false);
      }
    };

    container.addEventListener('focusin', handleFocusIn);
    container.addEventListener('focusout', handleFocusOut);
    container.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      container.removeEventListener('focusin', handleFocusIn);
      container.removeEventListener('focusout', handleFocusOut);
      container.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isReady, shouldMaintainFocus]);

  const handleSave = async () => {
    if (editorRef.current && onSave) {
      try {
        const data = await editorRef.current.save();

        // Convertir JSON a HTML usando la utilidad centralizada
        const htmlString = convertEditorJSONToHTML(data);

        // Pasar el HTML en lugar del JSON
        onSave(htmlString);
      } catch (error) {
        console.error('Error saving template:', error);
        toast.error('Error al guardar la plantilla');
      }
    }
  };

  const buildContractPreviewHtml = (html: string, zoom = 1, previewWatermarkImage: string | null = null): string => {
    const pageBreakToken = '__ATTIVA_PAGE_BREAK__';
    const normalizedZoom = Math.min(1.6, Math.max(0.6, zoom));
    const normalizedContent = (html || '')
      .replace(/\{\{\s*PAGE_BREAK\s*\}\}/gi, pageBreakToken)
      .replace(/{{PAGE_BREAK}}/gi, pageBreakToken);
    const safeWatermarkImage = previewWatermarkImage
      ? JSON.stringify(previewWatermarkImage).slice(1, -1)
      : '';

    const pages = normalizedContent.split(pageBreakToken).map((page) => page.trim()).filter((page) => page !== '');
    const pagesToRender = pages.length > 0 ? pages : ['<p>&nbsp;</p>'];

    const signatureFooter = `
      <div class="contract-signature-footer">
        <div class="signature-column">
          <div class="signature-square"></div>
          <div class="signature-label">Firma cliente</div>
        </div>
        <div class="signature-column">
          <div class="signature-square"></div>
          <div class="signature-label">Firma empresa</div>
        </div>
      </div>
    `;

    const renderedPages = pagesToRender.map((page, index) => `
      <section class="contract-page" data-page="${index + 1}">
        ${safeWatermarkImage ? `<div class="contract-page-watermark"><img src="${safeWatermarkImage}" alt="Marca de agua del contrato" /></div>` : ''}
        <div class="contract-page-content">${page}</div>
        ${signatureFooter}
      </section>
    `).join('');

    return `
      <!doctype html>
      <html lang="es">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 0;
              font-family: Inter, Arial, sans-serif;
              background: #eef2f7;
              color: #111827;
              padding: 24px;
            }
            .contract-preview-zoom {
              zoom: ${normalizedZoom};
            }
            @supports not (zoom: 1) {
              .contract-preview-zoom {
                transform: scale(${normalizedZoom});
                transform-origin: top center;
                width: calc(100% / ${normalizedZoom});
              }
            }
            .contract-page {
              width: 210mm;
              min-height: 297mm;
              margin: 0 auto 20px;
              background: #fff;
              box-shadow: 0 6px 18px rgba(15, 23, 42, 0.12);
              border: 1px solid #e5e7eb;
              position: relative;
              padding: 20mm 12mm 34mm;
            }
            .contract-page-watermark {
              position: absolute;
              top: 20mm;
              left: 12mm;
              right: 12mm;
              bottom: 34mm;
              display: flex;
              align-items: center;
              justify-content: center;
              pointer-events: none;
              z-index: 0;
            }
            .contract-page-watermark img {
              max-width: 78%;
              max-height: 78%;
              object-fit: contain;
              opacity: 0.12;
            }
            .contract-page-content {
              font-size: 13px;
              line-height: 1.5;
              word-break: break-word;
              position: relative;
              z-index: 1;
            }
            .contract-page-content table {
              width: 100%;
              border-collapse: collapse;
            }
            .contract-page-content table, .contract-page-content th, .contract-page-content td {
              border: 1px solid #d1d5db;
            }
            .contract-page-content th, .contract-page-content td {
              padding: 6px;
            }
            .contract-signature-footer {
              position: absolute;
              left: 12mm;
              right: 12mm;
              bottom: 8mm;
              display: flex;
              gap: 14px;
              z-index: 1;
            }
            .signature-column { flex: 1; }
            .signature-square {
              border: 1px solid #1f2937;
              height: 56px;
              background: #fff;
            }
            .signature-label {
              font-size: 10px;
              margin-top: 3px;
              text-align: center;
            }
            @media (max-width: 1100px) {
              body { padding: 8px; }
              .contract-page {
                width: 100%;
                min-height: auto;
              }
            }
          </style>
        </head>
        <body>
          <div class="contract-preview-zoom">
            ${renderedPages}
          </div>
        </body>
      </html>
    `;
  };

  useEffect(() => {
    if (!showPreview || !previewTemplateContent) {
      return;
    }
    setPreviewHtml(buildContractPreviewHtml(previewTemplateContent, previewZoom, watermarkImage));
  }, [showPreview, previewTemplateContent, previewZoom, watermarkImage]);

  const handlePreview = async () => {
    if (!editorRef.current) {
      toast.error('Editor no disponible');
      return;
    }

    setIsPreviewLoading(true);
    try {
      const data = await editorRef.current.save();
      const htmlString = convertEditorJSONToHTML(data);
      let renderedTemplateHtml = htmlString;

      if (type === 'contract' && selectedPreviewContractId) {
        try {
          const response = await (await (new ProductService()).previewContractTemplate(
            htmlString,
            selectedPreviewContractId
          )).getResponseData();

          if (response?.success && response?.data?.html) {
            renderedTemplateHtml = response.data.html;
          }
        } catch (error) {
          console.error('Error rendering contract template preview with real data:', error);
          toast.error('No se ha podido renderizar con datos reales. Se muestra la vista base.');
        }
      }

      setPreviewTemplateContent(renderedTemplateHtml);
      setPreviewHtml(buildContractPreviewHtml(renderedTemplateHtml, previewZoom, watermarkImage));
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating contract preview:', error);
      toast.error('No se pudo generar la previsualización');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handlePaste = async () => {
    try {
      // Verificar permisos del portapapeles
      if (!navigator.clipboard) {
        throw new Error('Clipboard API not supported');
      }

      // Obtener contenido del portapapeles
      const clipboardText = await navigator.clipboard.readText();

      if (!clipboardText || clipboardText.trim() === '') {
        toast.error('No hay contenido en el portapapeles');
        return;
      }

      if (editorRef.current) {
        const editor = editorRef.current as any;
        const blocksAPI = editor.blocks;
        const caretAPI = editor.caret;

        if (blocksAPI && caretAPI) {
          try {
            // Obtener el bloque actual
            const currentBlockIndex = await blocksAPI.getCurrentBlockIndex();

            if (currentBlockIndex >= 0) {
              const currentBlock = blocksAPI.getBlockByIndex(currentBlockIndex);

              if (currentBlock) {
                // Obtener la posición actual del caret
                const caretPosition = caretAPI.getOffset() || 0;

                // Obtener el contenido actual del bloque
                const blockData = await currentBlock.save();
                let currentText = blockData.data.text || '';

                // Insertar el contenido del portapapeles en la posición del cursor
                const newText = currentText.slice(0, caretPosition) + clipboardText + currentText.slice(caretPosition);

                // Actualizar el contenido del bloque
                await blocksAPI.update(currentBlockIndex, {
                  text: newText
                });

                // Posicionar el cursor después del contenido pegado
                setTimeout(() => {
                  try {
                    caretAPI.setToBlock(currentBlockIndex, caretPosition + clipboardText.length);
                  } catch (caretError) {
                    console.warn('Could not set caret position:', caretError);
                  }
                }, 100);

                toast.success('Contenido pegado correctamente');
                return;
              }
            }

            // Si no hay bloque actual o hay error, crear uno nuevo
            await blocksAPI.insert('paragraph', {
              text: clipboardText
            });
            toast.success('Contenido pegado correctamente');

          } catch (editorError) {
            console.error('Editor operation failed:', editorError);
            // Fallback: insertar como nuevo bloque
            await blocksAPI.insert('paragraph', {
              text: clipboardText
            });
            toast.success('Contenido pegado correctamente');
          }
        } else {
          throw new Error('Editor APIs not available');
        }
      } else {
        throw new Error('Editor not available');
      }
    } catch (error) {
      console.error('Error pasting content:', error);

      // Mensaje de error más específico
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        toast.error('Permisos de portapapeles denegados. Por favor, permite el acceso al portapapeles.');
      } else if (error instanceof Error && error.message.includes('Clipboard API not supported')) {
        toast.error('Tu navegador no soporta la funcionalidad de portapapeles.');
      } else {
        toast.error('Error al pegar el contenido. Intenta usar Ctrl+V directamente en el editor.');
      }
    }
  };

  const insertVariable = async (variable: string) => {
    try {
      if (editorRef.current) {
        const editor = editorRef.current as any;
        const blocksAPI = editor.blocks;
        const caretAPI = editor.caret;

        if (blocksAPI && caretAPI) {
          // Obtener el bloque actual
          const currentBlockIndex = await blocksAPI.getCurrentBlockIndex();
          const currentBlock = blocksAPI.getBlockByIndex(currentBlockIndex);

          if (currentBlock) {
            // Obtener la posición actual del caret
            const caretPosition = caretAPI.getOffset();

            // Obtener el contenido actual del bloque
            const blockData = await currentBlock.save();
            let currentText = blockData.data.text || '';

            // Insertar la variable en la posición del cursor
            const newText = currentText.slice(0, caretPosition) + variable + currentText.slice(caretPosition);

            // Actualizar el contenido del bloque
            await blocksAPI.update(currentBlockIndex, {
              text: newText
            });

            // Posicionar el cursor después de la variable insertada
            setTimeout(() => {
              caretAPI.setToBlock(currentBlockIndex, caretPosition + variable.length);
            }, 50);

            setShowVariables(false);
            toast.success(`Variable ${variable} insertada en el editor.`);
          } else {
            // Si no hay bloque actual, crear uno nuevo
            await blocksAPI.insert('paragraph', {
              text: variable
            });
            setShowVariables(false);
            toast.success(`Variable ${variable} insertada en el editor.`);
          }
        } else {
          throw new Error('Editor APIs not available');
        }
      } else {
        throw new Error('Editor not available');
      }
    } catch (error) {
      console.error('Error inserting variable:', error);
      // Fallback: copiar al portapapeles en caso de error
      try {
        await navigator.clipboard.writeText(variable);
        setShowVariables(false);
        toast.success(`Variable ${variable} copiada al portapapeles. Pégala donde desees en el editor.`);
      } catch (clipboardError) {
        toast.error('Error al insertar la variable');
      }
    }
  };

  return (
    <div className={`contract-template-editor ${className}`}>
      {/* Barra de herramientas */}
      {!readOnly && (
        <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded-lg border">
          {title && <h2 className="text-lg font-semibold">{title}</h2>}
          {canUseVariables && (
            <Button
              size="sm"
              variant={showVariables ? 'solid' : 'outline'}
              onClick={() => { setShowVariables(!showVariables) }}
              className="flex items-center gap-2"
            >
              <FaCode className="w-4 h-4" />
              Variables
            </Button>
          )}

          <div className="flex items-center gap-2 ml-auto">
            {type === 'contract' && (
              <div className="min-w-[280px]">
                <Select
                  placeholder="Contrato para previsualizar"
                  value={selectedPreviewContractId
                    ? previewContractOptions.find((option) => option.value === selectedPreviewContractId) || null
                    : null}
                  onChange={(selected: any) => setSelectedPreviewContractId(selected?.value ?? null)}
                  options={previewContractOptions}
                />
              </div>
            )}

            {type === 'contract' && (
              <Button
                size="sm"
                onClick={handlePaste}
                className="flex items-center gap-2"
                color='primary'
                variant='outline'
              >
                <FaPaste className="w-4 h-4" />
                Pegar
              </Button>
            )}

            {type === 'contract' && (
              <Button
                size="sm"
                onClick={handlePreview}
                className="flex items-center gap-2"
                color='secondary'
                variant='outline'
                isLoading={isPreviewLoading}
              >
                Previsualizar
              </Button>
            )}

            {onSave && (
              <Button
                size="sm"
                onClick={handleSave}
                className="flex items-center gap-2"
                color='secondary'
                variant='outline'
                disabled={disabledSaving}
              >
                Guardar Plantilla
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Panel de variables dinámicas */}
      {showVariables && canUseVariables && (
        <div className="mb-4 p-4 bg-primary-50 rounded-lg border border-primary-200">
          <h3 className="font-semibold text-primary-900 mb-3">Variables Dinámicas</h3>

          {/* Selector de categoría */}
          <div className="flex flex-wrap gap-2 mb-3">
            {visibleVariableCategories.map((variable) => (
              <button
                key={variable.type}
                type='button'
                onClick={() => setSelectedCategory(variable.type)}
                className={`px-3 py-1 rounded text-sm ${selectedCategory === variable.type
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-primary-600 border border-primary-300 hover:bg-primary-50'
                  }`}
              >
                {variable.label}
              </button>
            ))}
          </div>

          {/* Variables de la categoría seleccionada */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {visibleVariableCategories
              ?.find((cat) => cat.type === selectedCategory)
              ?.variables?.map((variable, index) => (
                <button
                  key={index}
                  type='button'
                  onClick={() => insertVariable(variable.key)}
                  className="p-2 text-left bg-white border border-gray-200 rounded hover:bg-primary-50 hover:border-primary-300 transition-colors"
                  title={`Insertar: ${variable.key}`}
                >
                  <div className="text-sm font-medium text-gray-900">
                    {variable.label}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {variable.key}
                  </div>
                </button>
              )) || (
                <div className="col-span-full text-center text-gray-500 py-4">
                  No hay variables disponibles para esta categoría
                </div>
              )}
          </div>
        </div>
      )}

      {/* Editor */}
      <div className="border rounded-lg" ref={editorContainerRef}>
        <div
          id="contract-editor"
          className="min-h-[200px] w-full p-4"
        /* style={{
          fontSize: '14px',
          lineHeight: '1.6'
        }} */
        />
      </div>

      {/* Información de ayuda */}
      {!readOnly && type === 'contract' && (
        <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
          <p><strong>Ayuda:</strong></p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>Haga click en las <strong>Variables</strong> para insertarlas automáticamente en el editor</li>
            <li>Haga click en las <strong>Marcas de Firma</strong> para insertarlas automáticamente donde se requieren firmas</li>
            <li>Las variables y marcas se insertan en la posición actual del cursor en el editor</li>
            <li>El editor soporta formato de texto, listas, tablas y otros elementos</li>
          </ul>
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 z-[1200] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white w-[95vw] h-[92vh] rounded-md shadow-xl flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Previsualización de plantilla de contrato</h3>
              <div className="flex items-center gap-2">
                <div className="hidden md:flex items-center gap-1 rounded border border-gray-300 bg-white px-1 py-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPreviewZoom((current) => Math.max(0.6, +(current - 0.1).toFixed(2)))}
                    disabled={previewZoom <= 0.6}
                    className="!px-2"
                  >
                    <FaSearchMinus className="w-3.5 h-3.5" />
                  </Button>
                  <span className="min-w-[58px] text-center text-sm text-gray-700">
                    {Math.round(previewZoom * 100)}%
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPreviewZoom((current) => Math.min(1.6, +(current + 0.1).toFixed(2)))}
                    disabled={previewZoom >= 1.6}
                    className="!px-2"
                  >
                    <FaSearchPlus className="w-3.5 h-3.5" />
                  </Button>
                </div>

                <Button size="sm" variant="outline" onClick={() => setShowPreview(false)}>
                  Cerrar
                </Button>
              </div>
            </div>
            <div className="flex-1 bg-slate-100">
              <iframe
                title="Previsualización contrato"
                srcDoc={previewHtml}
                className="w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractTemplateWYSIWYGEditor;
export type { ContractTemplateWYSIWYGEditorProps };
